import {
  CatalogVariantList,
  FAIL_REASON_LABELS,
  FailReason,
  JOB_PURPOSE_LABELS,
  Job,
  JobDestinations,
  JobList,
  JobPurpose,
  JobResult,
  Product,
} from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { AffixInput, Field, SwitchRow } from '../../components/Form';
import { Combobox } from '../../components/Combobox';
import {
  filamentOption,
  printerOption,
  spoolOption,
  variantOption,
} from '../../components/pickers';
import { Panel } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { useFilaments, usePrinters } from '../../lib/catalog-api';
import { useSpools } from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { durationInput, formatHm, parseDecimal, parseDuration } from '../../lib/numbers';
import { destLongText, useInvalidateQueue, useTimeline, whenText } from '../../lib/queue-api';

const g = (n: number) => `${Math.round(n).toLocaleString('pt-BR')} g`;

const pad = (n: number) => String(n).padStart(2, '0');

/** `<input type="datetime-local">` value for a moment, in this device's time zone. */
function localInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO time from a datetime-local value; null when empty, invalid or in the future. */
function fromLocalInput(v: string): string | null {
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime()) || d.getTime() > Date.now() + 60_000) return null;
  return d.toISOString();
}

/**
 * "Quando" for a run marked after the fact. A queued run ("Já foi impressa") always needs the end;
 * a printing one ends now unless "alterar" is used.
 */
function useWhen(job: Job) {
  const queued = job.status === 'queued';
  const [open, setOpen] = useState(queued);
  // The moment the dialog opened: default end and the latest one allowed.
  const [opened] = useState(() => localInput(new Date()));
  const [end, setEnd] = useState(opened);
  const finishedAt = open ? fromLocalInput(end) : null;
  const field = (
    <Field
      id="when-end"
      label={queued ? 'Terminou em' : 'Quando terminou'}
      error={open && !finishedAt ? 'Informe uma data e hora que já passou.' : null}
      help={queued ? 'A data de quando a impressão realmente terminou.' : undefined}
    >
      {open ? (
        <input
          id="when-end"
          type="datetime-local"
          className="input"
          value={end}
          max={opened}
          onChange={(e) => setEnd(e.target.value)}
        />
      ) : (
        <button
          id="when-end"
          type="button"
          className="btn sm g"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setOpen(true)}
        >
          Terminou agora · alterar
        </button>
      )}
    </Field>
  );
  return { field, past: open, finishedAt, valid: !open || finishedAt !== null };
}

/** "Concluir J-0213" (boards Fila-Desktop, Fila-Dialogos). */
export function FinishDialog({
  job,
  next,
  onClose,
}: {
  job: Job;
  next: Job | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const [time, setTime] = useState(durationInput(job.estSeconds));
  const [grams, setGrams] = useState<Record<string, string>>(
    Object.fromEntries(job.filaments.map((f) => [f.id, String(f.estGrams).replace('.', ',')])),
  );
  const [defects, setDefects] = useState('0');
  const [dest, setDest] = useState('planned');
  const [startNext, setStartNext] = useState(!!next);
  const when = useWhen(job);
  const printers = usePrinters();
  const meter = printers.data?.data.find((p) => p.id === job.printer?.id)?.hourMeterHours;
  const single = job.plateCount === 1 && !!job.variant;
  const destinations = useQuery({
    queryKey: ['jobs', job.id, 'destinations'],
    queryFn: () => api.get(`/jobs/${job.id}/destinations`, JobDestinations),
    enabled: single && job.stockUnits > 0,
  });
  const goodTo =
    dest === 'stock'
      ? { kind: 'stock' as const }
      : dest.startsWith('order:')
        ? { kind: 'order' as const, orderItemId: dest.slice(6) }
        : null;
  const chosen = destinations.data?.data.find((d) => `order:${d.orderItemId}` === dest);
  const seconds = parseDuration(time);
  const defectN = /^\d+$/.test(defects.trim()) ? Number(defects) : null;
  const parsed = job.filaments.map((f) => ({ f, grams: parseDecimal(grams[f.id] ?? '') }));
  const valid =
    seconds !== null &&
    defectN !== null &&
    defectN <= job.units &&
    parsed.every((p) => p.grams !== null) &&
    when.valid;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/jobs/${job.id}/finish`, JobResult, {
        actualSeconds: seconds,
        grams: parsed.map((p) => ({ lineId: p.f.id, grams: p.grams })),
        defectUnits: defectN,
        startNext: startNext && !when.past,
        goodTo,
        ...(when.finishedAt ? { finishedAt: when.finishedAt } : {}),
      }),
    onSuccess: async (r) => {
      await invalidate();
      const good = job.units - (defectN ?? 0);
      if (goodTo) {
        toast(
          `${job.number} concluída. ${
            goodTo.kind === 'stock'
              ? `${good} un. na pronta entrega`
              : `${Math.min(good, chosen?.needUnits ?? good)} un. separadas para o ${chosen?.orderNumber ?? 'pedido'}`
          }.${r.next ? ` ${r.next.number} começou.` : ''}${r.nextBlocked ? ` ${r.nextBlocked}` : ''}`,
        );
        onClose();
        return;
      }
      toast(
        `${job.number} concluída.${r.stockAdded ? ` ${r.stockAdded} un. na pronta entrega.` : ''} ${r.next ? `${r.next.number} começou.` : `${job.printer?.name ?? 'Impressora'} livre.`}${r.nextBlocked ? ` ${r.nextBlocked}` : ''}`.replace(
          'concluída.',
          good < job.units
            ? `concluída (${good} boas, ${job.units - good} com defeito).`
            : 'concluída.',
        ),
      );
      onClose();
    },
  });
  return (
    <Panel
      label={job.status === 'queued' ? `Já foi impressa: ${job.number}` : `Concluir ${job.number}`}
      onClose={onClose}
    >
      <p className="sm muted">
        {job.label}. Confira com o que o fatiador e a impressora mostram. Já vem preenchido com o
        estimado.
      </p>
      {when.field}
      <Field
        id="fin-time"
        label="Tempo real"
        help={`estimado ${formatHm(job.estSeconds)}`}
        error={seconds === null ? 'Use 6h18 ou 6:18.' : null}
      >
        <AffixInput id="fin-time" value={time} onChange={setTime} inputMode="text" />
      </Field>
      {parsed.map((p) => (
        <Field
          key={p.f.id}
          id={`fin-g-${p.f.id}`}
          label={`${p.f.spool?.code ?? 'Sem carretel'} · ${p.f.filament.name}`}
          help={`estimado ${g(p.f.estGrams)}`}
        >
          <AffixInput
            id={`fin-g-${p.f.id}`}
            unit="g"
            value={grams[p.f.id] ?? ''}
            invalid={p.grams === null}
            onChange={(v) => setGrams({ ...grams, [p.f.id]: v })}
          />
        </Field>
      ))}
      <Field id="fin-dest" label="Peças boas vão para">
        <select
          id="fin-dest"
          className="input"
          value={dest}
          onChange={(e) => setDest(e.target.value)}
          disabled={!single}
        >
          <option value="planned">{destLongText(job)}</option>
          {single && job.orders.length ? (
            <option value="stock">Pronta entrega · {job.units} un.</option>
          ) : null}
          {(destinations.data?.data ?? []).map((d) => (
            <option key={d.orderItemId} value={`order:${d.orderItemId}`}>
              Pedido em aberto · {d.orderNumber}
              {d.customer ? ` · ${d.customer}` : ''} · faltam {d.needUnits}
            </option>
          ))}
          {single && job.stockUnits > 0 && destinations.data?.data.length === 0 ? (
            <option disabled value="none">
              Pedido em aberto · nenhum precisa desta peça agora
            </option>
          ) : null}
        </select>
      </Field>
      <Field
        id="fin-def"
        label="Peças com defeito"
        help={`de ${job.units}; ${job.units - (defectN ?? 0)} boas + ${defectN ?? 0} com defeito`}
      >
        <AffixInput
          id="fin-def"
          unit="un."
          value={defects}
          onChange={setDefects}
          inputMode="numeric"
          invalid={defectN === null || defectN > job.units}
        />
      </Field>
      <div className="card sunk col" style={{ gap: 4, padding: 12, borderRadius: 12 }}>
        <span className="sm">
          Carretéis: {parsed.map((p) => `− ${g(p.grams ?? 0)}`).join(', ')}
        </span>
        <span className="sm">
          Horímetro {job.printer?.name ?? ''}: +{' '}
          {((seconds ?? 0) / 3600).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h
          {meter !== undefined
            ? ` → ${(meter + (seconds ?? 0) / 3600).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`
            : ''}
        </span>
      </div>
      {next && !when.past ? (
        <SwitchRow
          on={startNext}
          onChange={setStartNext}
          label={`Iniciar a próxima (${next.number}) agora`}
        />
      ) : null}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={!valid || save.isPending}
        onClick={() => save.mutate()}
      >
        Concluir
      </button>
    </Panel>
  );
}

/** "Falhou: J-0213". */
export function FailDialog({
  job,
  next,
  onClose,
}: {
  job: Job;
  next: Job | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const [pct, setPct] = useState(40);
  const [reason, setReason] = useState<FailReason>('adhesion');
  const [wasted, setWasted] = useState<string | null>(null);
  const [requeue, setRequeue] = useState(true);
  const [startNext, setStartNext] = useState(!!next);
  const when = useWhen(job);
  const auto = Math.round((job.estGrams * pct) / 100);
  const wastedN = wasted === null ? auto : parseDecimal(wasted);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/jobs/${job.id}/fail`, JobResult, {
        pct,
        reason,
        wastedGrams: wastedN,
        requeue,
        startNext: startNext && !when.past,
        ...(when.finishedAt ? { finishedAt: when.finishedAt } : {}),
      }),
    onSuccess: async (r) => {
      await invalidate();
      toast(
        `Falha registrada em ${job.number}${r.redo ? `, ${r.redo.number} no topo da fila` : ''}.${r.next ? ` ${r.next.number} começou.` : ''}${r.nextBlocked ? ` ${r.nextBlocked}` : ''}`,
      );
      onClose();
    },
  });
  return (
    <Panel label={`Falhou: ${job.number}`} onClose={onClose}>
      <p className="sm muted">{job.label}</p>
      {when.field}
      <span className="lbl">Quanto chegou a imprimir</span>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <button
          type="button"
          className="btn icon"
          aria-label="Menos 5%"
          onClick={() => setPct(Math.max(0, pct - 5))}
        >
          −
        </button>
        <div className="layers thin grow">
          <div className="fill" style={{ width: `${pct}%` }} />
          <div className="nozzle" style={{ left: `${pct}%` }} />
        </div>
        <button
          type="button"
          className="btn icon"
          aria-label="Mais 5%"
          onClick={() => setPct(Math.min(100, pct + 5))}
        >
          +
        </button>
        <b className="tab" style={{ minWidth: 44, textAlign: 'right' }}>
          {pct}%
        </b>
      </div>
      <span className="lbl">Motivo</span>
      <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Motivo">
        {FailReason.options.map((r) => (
          <button
            key={r}
            type="button"
            role="radio"
            aria-checked={reason === r}
            className={reason === r ? 'chip on' : 'chip'}
            onClick={() => setReason(r)}
          >
            {FAIL_REASON_LABELS[r]}
          </button>
        ))}
      </div>
      <Field
        id="fail-g"
        label="Filamento perdido"
        help={wasted === null ? `calculado de ${pct}%` : 'ajustado à mão'}
      >
        <AffixInput
          id="fail-g"
          unit="g"
          value={wasted ?? String(auto)}
          invalid={wastedN === null}
          onChange={(v) => setWasted(v)}
        />
      </Field>
      <SwitchRow on={requeue} onChange={setRequeue} label="Recolocar na fila, no topo" />
      {next && !when.past ? (
        <SwitchRow
          on={startNext}
          onChange={setStartNext}
          label={
            requeue
              ? `Iniciar a próxima da fila (${next.number}) agora (a refeita fica no topo, para depois)`
              : `Iniciar a próxima da fila (${next.number}) agora`
          }
        />
      ) : null}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn d block"
        disabled={wastedN === null || !when.valid || save.isPending}
        onClick={() => save.mutate()}
      >
        Registrar falha
      </button>
    </Panel>
  );
}

/** "Imprimir para estoque" / "Nova impressão". */
export function PrintForStockDialog({
  onClose,
  variantId: initial,
}: {
  onClose: () => void;
  variantId?: string;
}) {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const catalog = useQuery({
    queryKey: ['products', 'catalog', ''],
    queryFn: () => api.get('/variants', CatalogVariantList),
  });
  const printers = usePrinters();
  const timeline = useTimeline();
  const options = (catalog.data?.data ?? []).filter((c) => !c.missing);
  const [variantId, setVariantId] = useState(initial ?? '');
  const [units, setUnits] = useState('2');
  const [printerId, setPrinterId] = useState('');
  const [idem] = useState(newIdempotencyKey);
  const chosen = variantId || options[0]?.variant.id || '';
  const n = /^\d+$/.test(units.trim()) ? Number(units) : null;
  const preview = useQuery({
    queryKey: ['products', 'variant', chosen],
    queryFn: () =>
      api.get(`/products/${options.find((o) => o.variant.id === chosen)!.product.id}`, Product),
    enabled: !!chosen && options.some((o) => o.variant.id === chosen),
  });
  const v = preview.data?.variants.find((x) => x.id === chosen);
  const plates = v?.profile.plates.filter((p) => p.slots.some((s) => s.grams > 0)) ?? [];
  const runs = n ? plates.map((p) => Math.ceil(n / Math.max(1, p.unitsPerPlate))) : [];
  const seconds = plates.reduce((t, p, i) => t + (runs[i] ?? 0) * p.printSeconds, 0);
  const grams = plates.reduce(
    (t, p, i) => t + (runs[i] ?? 0) * p.slots.reduce((a, s) => a + s.grams, 0),
    0,
  );
  const lane = timeline.data?.printers.find(
    (p) => p.printer.id === (printerId || timeline.data?.printers[0]?.printer.id),
  );
  const readyAt = lane
    ? new Date(new Date(lane.freeAt).getTime() + seconds * 1000).toISOString()
    : null;
  const save = useMutation({
    mutationFn: () =>
      api.post(
        '/jobs',
        JobList,
        { variantId: chosen, units: n, printerId: printerId || null },
        { idempotencyKey: idem },
      ),
    onSuccess: (r) => {
      void invalidate();
      const first = r.data[0];
      toast(
        r.data.length === 1 && first
          ? `${first.number} · ${first.label} no fim da fila.`
          : `${r.data.length} impressões no fim da fila.`,
      );
      onClose();
    },
  });
  return (
    <Panel label="Imprimir para estoque" onClose={onClose}>
      <Field id="ps-v" label="Produto e variação">
        <Combobox
          id="ps-v"
          value={chosen}
          options={options.map(variantOption)}
          onChange={setVariantId}
        />
      </Field>
      {catalog.isSuccess && options.length === 0 ? (
        <p className="sm muted">Nenhum produto ativo com perfil de impressão completo.</p>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="ps-n" label="Unidades">
          <AffixInput
            id="ps-n"
            unit="un."
            value={units}
            onChange={setUnits}
            inputMode="numeric"
            invalid={!n}
          />
        </Field>
        <Field id="ps-p" label="Impressora">
          <Combobox
            id="ps-p"
            value={printerId}
            options={[
              { value: '', label: 'Automática', pinned: 'top' },
              ...(printers.data?.data ?? []).map(printerOption),
            ]}
            onChange={setPrinterId}
          />
        </Field>
      </div>
      {v && n ? (
        <div className="card sunk col" style={{ gap: 2, padding: 12, borderRadius: 12 }}>
          <div className="kv sm">
            <span className="muted">Placas</span>
            <span className="v">
              {runs.reduce((a, b) => a + b, 0)}{' '}
              {runs.reduce((a, b) => a + b, 0) === 1 ? 'placa' : 'placas'}
              {plates.length === 1 ? ` · ${plates[0]!.unitsPerPlate} por placa` : ''}
            </span>
          </div>
          <div className="kv sm">
            <span className="muted">Tempo</span>
            <span className="v">{formatHm(seconds)}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Filamento</span>
            <span className="v">{g(grams)}</span>
          </div>
          {readyAt ? (
            <div className="kv sm">
              <span className="muted">Fica pronto</span>
              <span className="v">{whenText(readyAt)}</span>
            </div>
          ) : null}
        </div>
      ) : null}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={!chosen || !n || save.isPending}
        onClick={() => save.mutate()}
      >
        Pôr no fim da fila
      </button>
    </Panel>
  );
}

interface PastLine {
  key: number;
  filamentId: string;
  /** '' = the queue's suggestion (loaded or opened spool), 'none' = do not deduct, else a spool id. */
  spool: string;
  grams: string;
}

/**
 * "Lançar impressão passada": a print that never went through the queue (a test, something kept
 * or given away, a failure). No board; same panel and fields as the other queue dialogs.
 */
export function PastPrintDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateQueue();
  const printers = usePrinters();
  const filaments = useFilaments();
  const spools = useSpools({ active: true });
  const [idem] = useState(newIdempotencyKey);
  const [printerId, setPrinterId] = useState('');
  const [label, setLabel] = useState('');
  const [purpose, setPurpose] = useState<JobPurpose>('test');
  const [failed, setFailed] = useState(false);
  const [reason, setReason] = useState<FailReason>('adhesion');
  const [pct, setPct] = useState('50');
  const [opened] = useState(() => localInput(new Date()));
  const [end, setEnd] = useState(opened);
  const [time, setTime] = useState('1h00');
  const [lines, setLines] = useState<PastLine[]>([
    { key: 1, filamentId: '', spool: '', grams: '' },
  ]);
  const [touched, setTouched] = useState(false);

  const printerList = printers.data?.data ?? [];
  const filamentList = filaments.data?.data ?? [];
  const chosenPrinter = printerId || printerList[0]?.id || '';
  const finishedAt = fromLocalInput(end);
  const seconds = parseDuration(time);
  const pctN = /^\d+$/.test(pct.trim()) && Number(pct) <= 100 ? Number(pct) : null;
  const parsed = lines.map((l) => ({
    ...l,
    filamentId: l.filamentId || filamentList[0]?.id || '',
    gramsN: parseDecimal(l.grams),
  }));
  const valid =
    !!chosenPrinter &&
    label.trim() !== '' &&
    finishedAt !== null &&
    seconds !== null &&
    seconds > 0 &&
    (!failed || pctN !== null) &&
    parsed.every((l) => l.filamentId && l.gramsN !== null);
  const set = (key: number, patch: Partial<PastLine>) =>
    setLines(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const save = useMutation({
    mutationFn: () =>
      api.post(
        '/jobs/past',
        Job,
        {
          printerId: chosenPrinter,
          label: label.trim(),
          purpose,
          result: failed ? 'failed' : 'done',
          ...(failed ? { failReason: reason, failPct: pctN } : {}),
          finishedAt,
          seconds,
          lines: parsed.map((l) => ({
            filamentId: l.filamentId,
            grams: l.gramsN,
            ...(l.spool === 'none' ? { spoolId: null } : l.spool ? { spoolId: l.spool } : {}),
          })),
        },
        { idempotencyKey: idem },
      ),
    onSuccess: async (j) => {
      await invalidate();
      toast(`${j.number} lançada: ${j.label}.`);
      onClose();
    },
  });

  return (
    <Panel label="Lançar impressão passada" onClose={onClose}>
      <p className="sm muted">
        Para impressões que não passaram pela fila: testes, peças para vocês, presentes, falhas. O
        filamento sai dos carretéis, as horas entram na impressora e o custo vai para o resultado do
        mês em que foi impressa.
      </p>
      <Field
        id="pp-label"
        label="O que foi impresso"
        error={touched && !label.trim() ? 'Dê um nome.' : null}
      >
        <input
          id="pp-label"
          className="input"
          placeholder="Ex.: Teste de encaixe"
          maxLength={120}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <span className="lbl">Finalidade</span>
      <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Finalidade">
        {JobPurpose.options.map((p) => (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={purpose === p}
            className={purpose === p ? 'chip on' : 'chip'}
            onClick={() => setPurpose(p)}
          >
            {JOB_PURPOSE_LABELS[p]}
          </button>
        ))}
      </div>
      <div
        className="seg"
        role="radiogroup"
        aria-label="Resultado"
        style={{ alignSelf: 'flex-start' }}
      >
        <button
          type="button"
          role="radio"
          aria-checked={!failed}
          className={failed ? '' : 'on'}
          onClick={() => setFailed(false)}
        >
          Concluída
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={failed}
          className={failed ? 'on' : ''}
          onClick={() => setFailed(true)}
        >
          Falhou
        </button>
      </div>
      {failed ? (
        <>
          <div className="row wrap" style={{ gap: 6 }} role="radiogroup" aria-label="Motivo">
            {FailReason.options.map((r) => (
              <button
                key={r}
                type="button"
                role="radio"
                aria-checked={reason === r}
                className={reason === r ? 'chip on' : 'chip'}
                onClick={() => setReason(r)}
              >
                {FAIL_REASON_LABELS[r]}
              </button>
            ))}
          </div>
          <Field id="pp-pct" label="Chegou a imprimir">
            <AffixInput
              id="pp-pct"
              unit="%"
              value={pct}
              onChange={setPct}
              inputMode="numeric"
              invalid={pctN === null}
            />
          </Field>
        </>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field id="pp-printer" label="Impressora">
          <Combobox
            id="pp-printer"
            value={chosenPrinter}
            options={printerList.map(printerOption)}
            onChange={setPrinterId}
          />
        </Field>
        <Field
          id="pp-time"
          label="Duração"
          error={seconds === null || seconds === 0 ? 'Use 6h18 ou 6:18.' : null}
        >
          <AffixInput id="pp-time" value={time} onChange={setTime} inputMode="text" />
        </Field>
      </div>
      <Field
        id="pp-end"
        label="Terminou em"
        error={finishedAt === null ? 'Informe uma data e hora que já passou.' : null}
      >
        <input
          id="pp-end"
          type="datetime-local"
          className="input"
          value={end}
          max={opened}
          onChange={(e) => setEnd(e.target.value)}
        />
      </Field>
      <span className="lbl">Filamentos</span>
      {parsed.map((l, i) => {
        const options = (spools.data?.data ?? []).filter((s) => s.filament.id === l.filamentId);
        return (
          <div
            key={l.key}
            className="card sunk col"
            style={{ gap: 8, padding: 12, borderRadius: 12 }}
          >
            <div className="row" style={{ gap: 8 }}>
              <div className="grow" style={{ minWidth: 0 }}>
                <Combobox
                  ariaLabel={`Filamento ${i + 1}`}
                  value={l.filamentId}
                  options={filamentList.map(filamentOption)}
                  onChange={(v) => set(l.key, { filamentId: v, spool: '' })}
                />
              </div>
              <div style={{ width: 120 }}>
                <AffixInput
                  ariaLabel={`Gramas do filamento ${i + 1}`}
                  unit="g"
                  value={l.grams}
                  onChange={(v) => set(l.key, { grams: v })}
                  invalid={touched && l.gramsN === null}
                />
              </div>
              {parsed.length > 1 ? (
                <button
                  type="button"
                  className="btn icon g"
                  aria-label={`Tirar filamento ${i + 1}`}
                  onClick={() => setLines(lines.filter((x) => x.key !== l.key))}
                >
                  ×
                </button>
              ) : null}
            </div>
            <Combobox
              ariaLabel={`Carretel do filamento ${i + 1}`}
              value={l.spool}
              options={[
                {
                  value: '',
                  label: 'Descontar do carretel em uso (automático)',
                  pinned: 'top',
                },
                ...options.map(spoolOption),
                {
                  value: 'none',
                  label: 'Não descontar (o carretel já foi pesado)',
                  pinned: 'bottom',
                },
              ]}
              onChange={(v) => set(l.key, { spool: v })}
            />
          </div>
        );
      })}
      <button
        type="button"
        className="btn sm g"
        style={{ alignSelf: 'flex-start' }}
        onClick={() =>
          setLines([...lines, { key: Date.now(), filamentId: '', spool: '', grams: '' }])
        }
      >
        + Filamento
      </button>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      <button
        type="button"
        className="btn p block"
        disabled={save.isPending}
        onClick={() => {
          setTouched(true);
          if (valid) save.mutate();
        }}
      >
        Lançar impressão
      </button>
    </Panel>
  );
}
